// ============================================================================

import { candidateFileUnavailableReason } from './file-reference-health';

export interface QuoteHeroPosition {
    x: number;
    y: number;
}

export interface QuoteHeroCandidateDimensions {
    width: number;
    height: number;
    ratio: number;
}

export interface QuoteHeroPanGeometry {
    x: number;
    y: number;
    overflowX: number;
    overflowY: number;
}

export interface QuoteHeroRankedCandidate<T = any> {
    candidate: T;
    index: number;
    score: number;
}

export interface QuoteHeroScoreOptions {
    scopeContext?: any;
    [key: string]: any;
}

export interface QuoteHeroMovementOptions {
    width?: number;
    height?: number;
    zoom?: number;
    imageWidth?: number;
    imageHeight?: number;
    deltaX?: number;
    deltaY?: number;
    distancePx?: number;
    [key: string]: any;
}

// QUOTE HERO IMAGE SELECTION
// ----------------------------------------------------------------------------
// A quote cover needs a representative site view, not whichever attachment
// happened to be imported first. Visual AI may nominate a candidate, but this
// deterministic metadata pass is the final safety gate and offline fallback.
// ============================================================================

const DOCUMENT_EXTENSION = /\.(?:pdf|docx?|xlsx?|csv|txt|rtf|eml|msg|pptx?)$/i;
const DOCUMENT_OR_GRAPHIC_PATTERN = /\b(?:document|letter|email|form|invoice|receipt|report|certificate|quote|rfq|rfp|tender|correspondence|notice|agenda|minutes|schedule|plan|drawing|specification|approval|permit|table|text|page|scan|print|fax|memo|contract|agreement|screenshot|screen[ -]?shot|logo|icon|avatar|signature|letterhead|watermark|qr|barcode|placeholder|thumbnail)\b/i;
// A quote cover should establish the property before it documents the defect.
// Keep this as a deterministic metadata rule: Stage 1 already supplies labels
// and captions, so choosing a better hero must not spend another vision call.
const EXPLICIT_ESTABLISHING_PATTERN = /\b(?:street[ -]?view|streetscape|street frontage|across (?:the )?(?:street|road)|from (?:the )?(?:street|road)|whole (?:apartment |residential |commercial )?building|entire (?:apartment |residential |commercial )?building|full (?:front )?(?:building|facade|elevation)|establishing (?:photo|shot|view))\b/i;
const EXTERIOR_SITE_PATTERN = /\b(?:external|exterior|facade|frontage|front elevation|street|streetscape|building|property)\b/i;
const OVERVIEW_PATTERN = /\b(?:overview|context|wide|whole|full view|approach|establishing)\b/i;
const REPRESENTATIVE_SITE_PATTERN = /\b(?:site|building|elevation|entry|entrance|balcony|roof|courtyard|carpark|driveway|foyer|lobby|room|bathroom|kitchen|ceiling|wall)\b/i;
const INTERIOR_PATTERN = /\b(?:interior|internal|inside|room|bathroom|kitchen|laundry|hallway|foyer|lobby|ceiling)\b/i;
const TIGHT_COMPOSITION_PATTERN = /\b(?:close[ -]?up|macro|detail)\b/i;
const TIGHT_DETAIL_PATTERN = /\b(?:close[ -]?up|macro|detail|crack|spall|stain|leak|rust|corrosion|peeling|mould|mold|damage|defect|drain|pipe|gutter|hole|joint|threshold)\b/i;
const LOW_SIGNAL_PATTERN = /\b(?:blurry|blurred|out[ -]?of[ -]?focus|underexposed|overexposed|too dark|black frame|obscured)\b/i;

// A file name such as "01_Building_Front_Streetscape_Hero.JPG" joins its words
// with underscores, which \b treats as letters, so no word pattern matched it.
const asWords = (value: string): string => value.replace(/_+/g, ' ');

// A builder who names a photo as the cover has chosen it. Peter, 2026-10-28,
// of "01_Building_Front_Streetscape_Hero.JPG": "It should have known to choose
// this file/photo as the hero image since they bothered to name them". Read
// from the file name only, never from an AI label (photos keep their names
// for the cover choice: Peter's 2026-10-14 exception to contents-not-names).
const BUILDER_HERO_NAME_PATTERN = /\b(?:hero|cover (?:photo|image|shot))\b/i;

function builderNamedHero(candidate: any): boolean {
    if (!candidate || typeof candidate !== 'object') return false;
    return BUILDER_HERO_NAME_PATTERN.test(asWords([candidate.name, candidate.originalName, candidate.fileName].filter(Boolean).join(' ')));
}

function candidateText(candidate: any): string {
    if (typeof candidate === 'string') return asWords(candidate);
    if (!candidate || typeof candidate !== 'object') return '';
    return asWords([
        candidate.aiLabel,
        candidate.aiDescription,
        candidate.label,
        candidate.caption,
        candidate.name,
        candidate.originalName,
        candidate.fileName,
        candidate.description,
        candidate.context,
        candidate.sceneType,
        candidate.viewType,
        candidate.category,
        candidate.tags,
        candidate.analysis?.label,
        candidate.analysis?.description,
        candidate.analysis?.summary,
        candidate.analysis?.location,
        candidate.metadata?.caption,
        candidate.metadata?.description,
        candidate.metadata?.sceneType,
        candidate.metadata?.viewType,
        candidate.aiAtLocator,
        candidate.locator,
        candidate.kind,
        candidate.type,
    ].filter(Boolean).join(' '));
}

function candidateMime(candidate: any): string {
    if (typeof candidate === 'string') {
        const match = candidate.match(/^data:([^;,]+)/i);
        return match ? String(match[1]).toLowerCase() : '';
    }
    if (!candidate || typeof candidate !== 'object') return '';
    const mediaType = typeof candidate.mediaType === 'string' && candidate.mediaType.includes('/')
        ? candidate.mediaType
        : '';
    return String(candidate.contentType || candidate.mimeType || mediaType).toLowerCase();
}

function documentSignalText(candidate: any): string {
    if (typeof candidate === 'string') return candidate;
    if (!candidate || typeof candidate !== 'object') return '';
    return [
        candidate.aiLabel,
        candidate.label,
        candidate.name,
        candidate.originalName,
        candidate.fileName,
        candidate.kind,
        candidate.type,
    ].filter(Boolean).join(' ');
}

function hasDurableOrFullImageSource(candidate: any): boolean {
    if (typeof candidate === 'string') return candidate.trim().length > 0;
    if (!candidate || typeof candidate !== 'object') return false;
    return Boolean(
        candidate.data
        || candidate.dataUrl
        || candidate.originalData
        || candidate.cleanData
        || candidate.clientData
        || candidate.blobId
        || candidate.s3Key
        || candidate.url
    );
}

function isThumbnailOnly(candidate: any): boolean {
    return !!candidate
        && typeof candidate === 'object'
        && !!candidate.thumbnailUrl
        && !hasDurableOrFullImageSource(candidate);
}

function isQuoteMakerGeneratedHero(candidate: any): boolean {
    if (!candidate || typeof candidate !== 'object') return false;
    return candidate.addedFrom === 'stage5-hero'
        && /^quote-hero-/i.test(String(candidate.id || ''));
}

function explicitDimensions(candidate: any): QuoteHeroCandidateDimensions | null {
    if (!candidate || typeof candidate !== 'object') return null;
    const width = Number(candidate.width || candidate.pixelWidth || candidate.naturalWidth);
    const height = Number(candidate.height || candidate.pixelHeight || candidate.naturalHeight);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
    return { width, height, ratio: width / height };
}

function scopeContextText(scopeContext: any): string {
    const entries = Array.isArray(scopeContext) ? scopeContext : [scopeContext];
    return entries.flatMap(entry => {
        if (!entry) return [];
        if (typeof entry === 'string') return [entry];
        return [entry.title, entry.description, entry.clientDescription, entry.scopeObjective];
    }).filter(Boolean).join(' ').toLowerCase();
}

function meaningfulTokens(text: unknown): Set<string> {
    return new Set((String(text || '').toLowerCase().match(/[a-z][a-z0-9]{3,}/g) || [])
        .filter(token => !['photo', 'image', 'site', 'work', 'repair', 'project', 'scope', 'building'].includes(token)));
}

function contextOverlap(candidate: any, scopeContext: any): number {
    const candidateTokens = meaningfulTokens(candidateText(candidate));
    const scopeTokens = meaningfulTokens(scopeContextText(scopeContext));
    let overlap = 0;
    for (const token of candidateTokens) {
        if (scopeTokens.has(token)) overlap += 1;
    }
    return Math.min(8, overlap * 2);
}

/**
 * Rejection is intentionally conservative. A generic camera filename remains
 * eligible; we reject only affirmative evidence that a candidate is not a
 * usable client-facing site image.
 */
export function isQuoteHeroCandidateRejected(candidate: any): boolean {
    if (!candidate) return true;
    if (typeof candidate !== 'string' && typeof candidate !== 'object') return true;
    if (candidateFileUnavailableReason(candidate)) return true;

    const text = documentSignalText(candidate);
    const mime = candidateMime(candidate);
    if (DOCUMENT_EXTENSION.test(text)) return true;
    if (mime && !mime.startsWith('image/')) return true;
    if (typeof candidate === 'string' && candidate.startsWith('data:') && !mime.startsWith('image/')) return true;

    if (typeof candidate === 'object') {
        if (candidate.heroEligible === false
            || candidate.deleted === true
            || candidate.missing === true) return true;
        if (candidate.markedUp || candidate.aiAnnotated || candidate.manualEditedAt) return true;
        if (isThumbnailOnly(candidate)) return true;

        const dimensions = explicitDimensions(candidate);
        if (dimensions && (dimensions.width < 320 || dimensions.height < 180 || dimensions.width * dimensions.height < 90_000)) {
            return true;
        }
        const sizeKB = Number(candidate.sizeKB);
        if (Number.isFinite(sizeKB) && sizeKB > 0 && sizeKB < 12) return true;
    }

    // QuoteMaker's own persisted cover rows intentionally contain the word
    // "Quote" in both id/name. They have already crossed every hard rejection
    // above (availability, media type, deletion, markup, dimensions and size),
    // so the generic document-name heuristic must not discard the builder's
    // saved cover and auto-select/recompress a different photo on reopen.
    if (isQuoteMakerGeneratedHero(candidate)) return false;

    return DOCUMENT_OR_GRAPHIC_PATTERN.test(text);
}

export function scoreQuoteHeroCandidate(candidate: any, index = 0, options: QuoteHeroScoreOptions = {}): number {
    if (isQuoteHeroCandidateRejected(candidate)) return -1_000;

    const text = candidateText(candidate);
    let score = 30;
    // Above any score the patterns below can add, so the named photo wins.
    if (builderNamedHero(candidate)) score += 500;
    const tightComposition = TIGHT_COMPOSITION_PATTERN.test(text);
    const tightDetail = TIGHT_DETAIL_PATTERN.test(text);
    // Generic phrases such as "building exterior" also describe close-up
    // defect evidence. Reserve the premium for affirmative establishing-shot
    // language, and never grant it to a candidate labelled as a detail.
    const wholeBuildingExterior = EXPLICIT_ESTABLISHING_PATTERN.test(text) && !tightComposition;
    if (wholeBuildingExterior) {
        score += 76;
    } else {
        if (EXTERIOR_SITE_PATTERN.test(text)) score += 28;
        if (OVERVIEW_PATTERN.test(text)) score += 12;
        if (REPRESENTATIVE_SITE_PATTERN.test(text)) score += 8;
    }
    // An internal overview can be a valid fallback, but it should not beat an
    // available street/property establishing shot merely because it is wide.
    if (INTERIOR_PATTERN.test(text) && !wholeBuildingExterior) score -= 30;
    if (tightDetail) score -= 24;
    if (LOW_SIGNAL_PATTERN.test(text)) score -= 30;

    if (candidate && typeof candidate === 'object') {
        const dimensions = explicitDimensions(candidate);
        if (dimensions) {
            if (dimensions.ratio >= 1.25 && dimensions.ratio <= 2.4) score += 16;
            else if (dimensions.ratio < 0.82) score -= 12;
            else score += 3;
            if (dimensions.width >= 1_200 && dimensions.height >= 720) score += 7;
        }
        const sizeKB = Number(candidate.sizeKB);
        if (Number.isFinite(sizeKB) && sizeKB >= 100) score += 5;
        if (candidate.fromVideo) score -= REPRESENTATIVE_SITE_PATTERN.test(text) ? 4 : 14;
        if (candidate.thumbnailUrl && hasDurableOrFullImageSource(candidate)) score -= 1;
    }

    score += contextOverlap(candidate, options.scopeContext);
    // Import order settles a true tie but cannot overpower evidence quality.
    score += Math.max(0, 10 - Math.max(0, Number(index) || 0)) * 0.01;
    return score;
}

export function rankQuoteHeroCandidates<T = any>(candidates: T[] = [], options: QuoteHeroScoreOptions = {}): QuoteHeroRankedCandidate<T>[] {
    if (!Array.isArray(candidates)) return [];
    return candidates
        .map((candidate, index) => ({
            candidate,
            index,
            score: scoreQuoteHeroCandidate(candidate, index, options),
        }))
        .filter(entry => entry.score > -1_000)
        .sort((a, b) => (b.score - a.score) || (a.index - b.index));
}

export function chooseQuoteHeroPhotoIndex(candidates: any[] = [], preferredIndex: number | null = null, options: QuoteHeroScoreOptions = {}): number | null {
    const ranked = rankQuoteHeroCandidates(candidates, options);
    if (ranked.length === 0) return null;

    const best = ranked[0]!;
    if (Number.isInteger(preferredIndex) && preferredIndex! >= 0 && preferredIndex! < candidates.length) {
        const preferred = ranked.find(entry => entry.index === preferredIndex);
        // Trust vision when its choice is genuinely competitive; otherwise the
        // stronger representative overview wins.
        if (preferred && preferred.score >= 30 && preferred.score >= best.score - 12) {
            return preferred.index;
        }
    }
    return best.index;
}

function quoteHeroCandidateIdentity(candidate: any): string {
    if (typeof candidate === 'string') return `string:${candidate}`;
    if (!candidate || typeof candidate !== 'object') return '';
    return String(
        candidate.id
        || candidate.blobId
        || candidate.s3Key
        || candidate.s3_key
        || candidate.fileMetadataId
        || candidate.file_metadata_id
        || candidate.url
        || candidate.thumbnailUrl
        || candidate.name
        || '',
    );
}

/**
 * Fingerprint every value that can change deterministic hero ranking or
 * eligibility. Stage 1 enriches image metadata asynchronously, so identity and
 * dimensions alone would leave the first-file fallback cached after a caption
 * or scene classification identifies the proper establishing shot.
 */
export function quoteHeroCandidateFingerprint(candidates: any[] = []): string {
    return JSON.stringify((Array.isArray(candidates) ? candidates : []).map((candidate: any, index: number) => {
        if (typeof candidate === 'string') {
            return ['string', candidate.slice(0, 48), candidate.length];
        }
        if (!candidate || typeof candidate !== 'object') return ['invalid', index];
        return [
            quoteHeroCandidateIdentity(candidate) || index,
            candidateText(candidate),
            candidate.width ?? candidate.pixelWidth ?? candidate.naturalWidth ?? null,
            candidate.height ?? candidate.pixelHeight ?? candidate.naturalHeight ?? null,
            candidate.sizeKB ?? null,
            candidate.contentType ?? candidate.mimeType ?? candidate.mediaType ?? null,
            candidate.fromVideo === true,
            candidate.markedUp === true,
            candidate.aiAnnotated === true,
            candidate.heroEligible === false,
            candidate.deleted === true,
            candidate.missing === true,
            candidate.unavailableReason ?? null,
            candidate.availability_state ?? candidate.availabilityState ?? null,
            candidate.availability_reason ?? candidate.availabilityReason ?? null,
        ];
    }));
}

/**
 * Resolve the Stage 5 hero attempt order without letting a dead persisted
 * reference monopolise the fallback. A healthy stored choice remains first
 * (operator authority), but ranked live evidence is always retained behind it
 * in case its bytes cannot be resolved on this device.
 */
export function orderQuoteHeroCandidates<T = any>(candidates: T[] = [], options: { storedCandidate?: any; suggestedCandidate?: any; scopeContext?: any } = {}): T[] {
    const availableCandidates = Array.isArray(candidates) ? candidates : [];
    const ranked = rankQuoteHeroCandidates(availableCandidates, {
        scopeContext: options.scopeContext,
    });
    const ordered: T[] = [];
    const seen = new Set();
    const add = (candidate: any) => {
        if (!candidate || isQuoteHeroCandidateRejected(candidate)) return;
        const identity = quoteHeroCandidateIdentity(candidate) || `anonymous:${ordered.length}`;
        if (seen.has(identity)) return;
        seen.add(identity);
        ordered.push(candidate);
    };

    add(options.storedCandidate);

    const suggestedIdentity = quoteHeroCandidateIdentity(options.suggestedCandidate);
    const suggestedIndex = suggestedIdentity
        ? availableCandidates.findIndex(candidate => quoteHeroCandidateIdentity(candidate) === suggestedIdentity)
        : -1;
    if (ordered.length === 0 && options.suggestedCandidate && suggestedIndex < 0) {
        add(options.suggestedCandidate);
    }
    const chosenIndex = chooseQuoteHeroPhotoIndex(
        availableCandidates,
        suggestedIndex >= 0 ? suggestedIndex : null,
        { scopeContext: options.scopeContext },
    );
    if (chosenIndex != null) add(availableCandidates[chosenIndex]);
    for (const entry of ranked) add(entry.candidate);
    return ordered;
}

export function normaliseQuoteHeroPosition(position?: any): QuoteHeroPosition {
    const clamp = (value: unknown): number => Math.min(100, Math.max(0, Number(value)));
    const x = Number.isFinite(Number(position?.x)) ? clamp(position.x) : 50;
    const y = Number.isFinite(Number(position?.y)) ? clamp(position.y) : 50;
    return { x, y };
}

export const QUOTE_HERO_ZOOM_MIN = 1;
export const QUOTE_HERO_ZOOM_MAX = 2.5;
export const QUOTE_HERO_ZOOM_STEP = 0.1;
export const QUOTE_HERO_HEIGHT_MIN = 120;
export const QUOTE_HERO_HEIGHT_MAX = 400;
export const QUOTE_HERO_HEIGHT_DEFAULT = 240;

export function normaliseQuoteHeroZoom(value?: unknown): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return QUOTE_HERO_ZOOM_MIN;
    const clamped = Math.min(QUOTE_HERO_ZOOM_MAX, Math.max(QUOTE_HERO_ZOOM_MIN, numeric));
    return Math.round(clamped * 100) / 100;
}

export function adjustQuoteHeroZoom(value?: unknown, stepDirection?: unknown): number {
    const direction = Math.sign(Number(stepDirection) || 0);
    return normaliseQuoteHeroZoom(
        normaliseQuoteHeroZoom(value) + (direction * QUOTE_HERO_ZOOM_STEP),
    );
}

export function normaliseQuoteHeroHeight(value?: unknown): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return QUOTE_HERO_HEIGHT_DEFAULT;
    return Math.round(Math.min(QUOTE_HERO_HEIGHT_MAX, Math.max(QUOTE_HERO_HEIGHT_MIN, numeric)));
}

/**
 * Return the number of rendered pixels the focal coordinate can traverse on
 * each axis. `object-position` owns the cover overflow; scaling around the
 * same focal point contributes the transform overflow. Their combined span is
 * what must divide pointer movement for the image to stay under the pointer at
 * every supported zoom.
 */
export function quoteHeroPanGeometry(movement: QuoteHeroMovementOptions = {}): QuoteHeroPanGeometry {
    const width = Number(movement.width);
    const height = Number(movement.height);
    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
        return { x: 0, y: 0, overflowX: 0, overflowY: 0 };
    }

    const zoom = normaliseQuoteHeroZoom(movement.zoom);
    const imageWidth = Number(movement.imageWidth);
    const imageHeight = Number(movement.imageHeight);
    const hasIntrinsicSize = Number.isFinite(imageWidth) && imageWidth > 0
        && Number.isFinite(imageHeight) && imageHeight > 0;
    let overflowX = 0;
    let overflowY = 0;
    if (hasIntrinsicSize) {
        const coverScale = Math.max(width / imageWidth, height / imageHeight);
        overflowX = Math.max(0, (imageWidth * coverScale) - width);
        overflowY = Math.max(0, (imageHeight * coverScale) - height);
    }

    // A visible loaded image always supplies intrinsic dimensions. Preserve
    // the legacy 1:1 mapping only as a defensive pre-load fallback at 100%.
    const fallbackX = !hasIntrinsicSize && zoom === 1 ? width : 0;
    const fallbackY = !hasIntrinsicSize && zoom === 1 ? height : 0;
    return {
        x: Math.max(fallbackX, (zoom * overflowX) + ((zoom - 1) * width)),
        y: Math.max(fallbackY, (zoom * overflowY) + ((zoom - 1) * height)),
        overflowX,
        overflowY,
    };
}

/**
 * Map a direct pointer drag into the persisted object-position coordinate.
 * Dragging the visible image right reveals more of its left edge, so pointer
 * delta is subtracted from the crop focus. The helper is deliberately pure:
 * pointermove may mutate the image style cheaply, while pointerup commits the
 * one returned value through the quote state boundary.
 */
export function panQuoteHeroPosition(position?: any, movement: QuoteHeroMovementOptions = {}): QuoteHeroPosition {
    const start = normaliseQuoteHeroPosition(position);
    const width = Number(movement.width);
    const height = Number(movement.height);
    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
        return start;
    }
    const deltaX = Number.isFinite(Number(movement.deltaX)) ? Number(movement.deltaX) : 0;
    const deltaY = Number.isFinite(Number(movement.deltaY)) ? Number(movement.deltaY) : 0;
    const geometry = quoteHeroPanGeometry(movement);
    return normaliseQuoteHeroPosition({
        x: geometry.x > 0 ? start.x - ((deltaX / geometry.x) * 100) : start.x,
        y: geometry.y > 0 ? start.y - ((deltaY / geometry.y) * 100) : start.y,
    });
}

export const QUOTE_HERO_NUDGE_PIXELS = 12;

/** Move the rendered image (not the abstract focal coordinate) by one step. */
export function nudgeQuoteHeroPosition(position?: any, direction?: any, movement: QuoteHeroMovementOptions = {}): QuoteHeroPosition {
    const distance = Number.isFinite(Number(movement.distancePx))
        ? Math.max(1, Math.abs(Number(movement.distancePx)))
        : QUOTE_HERO_NUDGE_PIXELS;
    const pointerDelta = {
        left: { deltaX: -distance, deltaY: 0 },
        right: { deltaX: distance, deltaY: 0 },
        up: { deltaX: 0, deltaY: -distance },
        down: { deltaX: 0, deltaY: distance },
    }[direction as 'left' | 'right' | 'up' | 'down'];
    if (!pointerDelta) return normaliseQuoteHeroPosition(position);
    return panQuoteHeroPosition(position, { ...movement, ...pointerDelta });
}

export const quoteHeroSelectionInternals = Object.freeze({
    candidateText,
    candidateMime,
    explicitDimensions,
    hasDurableOrFullImageSource,
    isThumbnailOnly,
    isQuoteMakerGeneratedHero,
});
